import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import ModalComponent from "../Modal/Modal"
import Calendar from "../Calendar/Calendar"
import Input from "../Input/Input"
import Dropdown from "../Dropdown/Dropdown"
import { getReturnReasons } from "../../features/payment/paymentSlice"
import { useUiKit } from "../UiKit/UiKitContext"

// The due-date suggestion reads the stored date's UTC calendar day (due dates are
// noon-UTC anchored server-side) — don't rely on import order to have extended dayjs.
dayjs.extend(utc)

/**
 * Confirmation modal for manually marking a payment Paid (cleared) or Failed.
 *
 * The processed date is SUGGESTED from the payment's due date — an operator marking a
 * payment after the fact almost always means "it happened when it was due", not "it
 * happened the moment I clicked" — but stays fully editable. The optional transaction
 * id and (failed only) reason fields ride along to the same status endpoint, which
 * stamps them onto transactionData.
 *
 * The code is picked from the server's vocabulary, not typed: debitBlock refuses to act on a
 * code it cannot map. "No code available" keeps free text reachable — a fabricated category is
 * worse than a blank, because a later block would act on it.
 *
 * `payment` needs only { dueDate, amount }; `mode` is 'cleared' | 'failed'.
 * onConfirm receives { processedAt, transactionId, reasonText, reasonCode } with empty
 * fields omitted (processedAt is a YYYY-MM-DD string — the backend's dateParser
 * anchors it at business noon).
 */
const NO_CODE = '__none__'

const MarkPaymentStatusModal = ({ open, onClose, mode, payment, onConfirm }) => {
  const dispatch = useDispatch()
  const { button: kitButton } = useUiKit()
  const isFailed = mode === 'failed'

  const suggestedDate = payment?.dueDate ? dayjs.utc(payment.dueDate).format('YYYY-MM-DD') : ''
  const [processedAt, setProcessedAt] = useState(suggestedDate)
  const [transactionId, setTransactionId] = useState('')
  const [reasonText, setReasonText] = useState('')
  const [reasonCode, setReasonCode] = useState('')
  const [reasons, setReasons] = useState([])
  const [vocabularyFailed, setVocabularyFailed] = useState(false)

  useEffect(() => {
    if (!open || !isFailed) return
    let live = true
    const load = async () => {
      let list
      try {
        const res = await dispatch(getReturnReasons())
        list = res?.payload?.data
      } catch {
        list = null
      }
      if (!live) return
      if (Array.isArray(list) && list.length) {
        setReasons(list)
        setVocabularyFailed(false)
      } else {
        setReasons([])
        setVocabularyFailed(true)
      }
    }
    load()
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isFailed])

  const selectReturn = (value) => {
    if (value === NO_CODE) {
      setReasonCode('')
      setReasonText('')
      return
    }
    const chosen = reasons.find((r) => r.rCode === value)
    setReasonCode(value)
    if (chosen) setReasonText(chosen.text)
  }

  // Re-suggest whenever the modal is opened for a (possibly different) payment; also
  // drops stale edits from a previous open.
  useEffect(() => {
    if (!open) return
    setProcessedAt(payment?.dueDate ? dayjs.utc(payment.dueDate).format('YYYY-MM-DD') : '')
    setTransactionId('')
    setReasonText('')
    setReasonCode('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payment?.dueDate])

  const handleConfirm = () => {
    const fields = {}
    const dateValue = processedAt && dayjs(processedAt).isValid() ? dayjs(processedAt).format('YYYY-MM-DD') : null
    if (dateValue) fields.processedAt = dateValue
    if (transactionId.trim()) fields.transactionId = transactionId.trim()
    if (isFailed && reasonText.trim()) fields.reasonText = reasonText.trim()
    if (isFailed && reasonCode.trim()) fields.reasonCode = reasonCode.trim()
    return onConfirm(fields)
  }

  return (
    <ModalComponent
      title={isFailed ? 'Mark Payment as Failed' : 'Mark Payment as Paid'}
      modalButtonTitle={isFailed ? 'Mark Failed' : 'Mark Paid'}
      disableBtn={false}
      customOpenModal={open}
      onClose={onClose}
      functionButton={handleConfirm}
      awaitFunctionButton={kitButton}
    >
      <p className="text-sm text-text-secondary mb-4" data-testid="mark-status-hint">
        {`The payment${typeof payment?.amount === 'number' ? ` of $${payment.amount.toFixed(2)}` : ''} will be marked as ${isFailed ? 'failed' : 'paid'}. The processed date below is suggested from its due date — adjust it if it actually ${isFailed ? 'failed' : 'cleared'} on a different day.`}
      </p>

      <Calendar
        width="w-full"
        label={isFailed ? 'Failed On' : 'Processed On'}
        value={processedAt}
        onChange={(val) => setProcessedAt(val)}
        isRequired={true}
        timezone="system"
      />

      <Input
        placeholder="Gateway transaction id (optional)"
        parentClassName="mt-6"
        label="Transaction ID"
        value={transactionId}
        onChange={(val) => setTransactionId(val.target.value)}
      />

      {isFailed && (
        <>
          {vocabularyFailed ? (
            <Input
              placeholder="e.g. R02"
              parentClassName="mt-6"
              label="Reason Code"
              value={reasonCode}
              onChange={(val) => setReasonCode(val.target.value)}
            />
          ) : (
            <div className="mt-6">
              <Dropdown
                label="Reason Code"
                ariaLabel="Reason Code"
                placeholder="Select the bank's return"
                value={reasonCode}
                setValue={selectReturn}
                menuItems={[
                  ...reasons.map((r) => ({ value: r.rCode, label: `${r.rCode} — ${r.text}` })),
                  { value: NO_CODE, label: 'No code available' },
                ]}
              />
            </div>
          )}
          <Input
            placeholder="e.g. Insufficient funds"
            parentClassName="mt-6"
            label="Reason"
            value={reasonText}
            onChange={(val) => setReasonText(val.target.value)}
          />
        </>
      )}
    </ModalComponent>
  )
}

export default MarkPaymentStatusModal
